import { useState } from 'react';
import api from '../../../utils/axiosConfig';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Switch } from '../../ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { Loader2 } from 'lucide-react';
import AttributeValueBadge from '../AttributeValueBadge';
import { PERIOD_BUCKETS, normalizePeriodBuckets } from '../../../utils/periodizationPeriodBuckets';

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

const CATEGORIES = ['load', 'recovery', 'distribution', 'specificity', 'intensity', 'ancillary', 'custom'];

export default function AdminAttributeTypesSection({ types, schemas, onReload, saving, setSaving }) {
  const { t } = useLanguage();
  const [editingId, setEditingId] = useState(null);
  const emptyForm = () => ({
    type_key: '',
    name: '',
    description: '',
    value_schema_id: '',
    category: 'load',
    period_buckets: normalizePeriodBuckets({
      phase: true,
      week: true,
      day: false,
      session: false,
    }),
  });
  const [form, setForm] = useState(emptyForm());

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const startEdit = (type) => {
    setEditingId(type.id);
    setForm({
      type_key: type.type_key,
      name: type.name,
      description: type.description || '',
      value_schema_id: type.value_schema_id ? String(type.value_schema_id) : '',
      category: type.category || 'load',
      period_buckets: normalizePeriodBuckets(type.period_buckets_allowed || type.period_buckets),
    });
  };

  const toggleFormBucket = (bucket) => {
    setForm((prev) => ({
      ...prev,
      period_buckets: {
        ...normalizePeriodBuckets(prev.period_buckets),
        [bucket]: !normalizePeriodBuckets(prev.period_buckets)[bucket],
      },
    }));
  };

  const saveType = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/admin/periodization-catalog/attribute-types/${editingId}`, {
          name: form.name.trim(),
          description: form.description?.trim() || null,
          value_schema_id: form.value_schema_id ? parseInt(form.value_schema_id, 10) : undefined,
          category: form.category,
          period_buckets: form.period_buckets,
        });
      } else {
        if (!form.type_key.trim() || !form.value_schema_id) {
          window.alert(t('periodization.valueSchema.required'));
          setSaving(false);
          return;
        }
        await api.post('/api/admin/periodization-catalog/attribute-types', {
          type_key: form.type_key.trim(),
          name: form.name.trim(),
          description: form.description?.trim() || null,
          value_schema_id: parseInt(form.value_schema_id, 10),
          category: form.category,
          period_buckets: form.period_buckets,
        });
      }
      resetForm();
      await onReload?.();
    } catch (e) {
      window.alert(e.response?.data?.error || t('periodization.assessmentManager.typeSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>{t('periodization.admin.builtinTypesTitle')}</CardTitle>
          <CardDescription>{t('periodization.admin.typesTableHint')}</CardDescription>
        </CardHeader>
        <CardContent>
          {types.length === 0 ? (
            <p className='text-sm text-muted-foreground'>{t('periodization.admin.emptyTitle')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('periodization.admin.colName')}</TableHead>
                  <TableHead>{t('periodization.admin.colValueSet')}</TableHead>
                  <TableHead>{t('periodization.admin.colCategory')}</TableHead>
                  <TableHead className='text-right'>{t('common.edit')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((ty) => (
                  <TableRow key={ty.id} className={editingId === ty.id ? 'bg-primary/5' : undefined}>
                    <TableCell>
                      <div className='flex items-center gap-2'>
                        <AttributeValueBadge
                          value={
                            ty.value_schema?.schema_type === 'boolean'
                              ? true
                              : ty.value_schema?.config?.numeric?.default
                          }
                          type={ty}
                          compact
                        />
                        <div>
                          <p className='font-medium'>{attrLabel(t, ty.type_key, ty.name)}</p>
                          <p className='text-[10px] text-muted-foreground font-mono'>{ty.type_key}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{ty.value_schema?.name || '—'}</TableCell>
                    <TableCell className='capitalize text-muted-foreground'>{ty.category}</TableCell>
                    <TableCell className='text-right'>
                      <Button type='button' size='sm' variant='outline' onClick={() => startEdit(ty)}>
                        {t('common.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className='text-base'>
            {editingId ? t('periodization.assessmentManager.editType') : t('periodization.admin.addType')}
          </CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid gap-3 sm:grid-cols-2'>
            <div>
              <Label className='text-xs'>{t('periodization.assessmentManager.typeKey')}</Label>
              <Input
                className='mt-1 font-mono text-xs'
                value={form.type_key}
                onChange={(e) => setForm((p) => ({ ...p, type_key: e.target.value }))}
                disabled={!!editingId}
                readOnly={!!editingId}
              />
            </div>
            <div>
              <Label className='text-xs'>{t('periodization.assessmentManager.typeName')}</Label>
              <Input className='mt-1' value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className='sm:col-span-2'>
              <Label className='text-xs'>{t('periodization.admin.schemaDescription')}</Label>
              <Input
                className='mt-1'
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div>
              <Label className='text-xs'>{t('periodization.valueSchema.pickSet')}</Label>
              <Select
                value={form.value_schema_id}
                onValueChange={(v) => setForm((p) => ({ ...p, value_schema_id: v }))}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue placeholder={t('periodization.valueSchema.pickSet')} />
                </SelectTrigger>
                <SelectContent>
                  {schemas.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className='text-xs'>{t('periodization.admin.colCategory')}</Label>
              <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                <SelectTrigger className='mt-1'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <p className='text-xs font-medium mb-2'>{t('periodization.assessmentManager.bucketMatrixTitle')}</p>
            <div className='flex flex-wrap gap-3'>
              {PERIOD_BUCKETS.map((bucket) => (
                <label key={bucket} className='flex items-center gap-2 text-xs'>
                  <Switch
                    checked={!!normalizePeriodBuckets(form.period_buckets)[bucket]}
                    onCheckedChange={() => toggleFormBucket(bucket)}
                  />
                  {t(`periodization.assessmentManager.buckets.${bucket}`)}
                </label>
              ))}
            </div>
          </div>

          <div className='flex gap-2 justify-end'>
            {editingId && (
              <Button type='button' variant='ghost' onClick={resetForm}>
                {t('common.cancel')}
              </Button>
            )}
            <Button type='button' onClick={saveType} disabled={saving}>
              {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
